import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { NgxLoadingModule } from 'ngx-loading';
import { CurrencyPipe, PercentPipe } from '@angular/common';
import {
  FormGroup,
  FormsModule,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MaskitoDirective } from '@maskito/angular';
import { GridModule, KENDO_GRID } from '@progress/kendo-angular-grid';
import { HighchartsChartComponent } from 'highcharts-angular';
import { MaskService } from '../../services/mask.service';
import Highcharts from 'highcharts';
import { MatRadioModule } from '@angular/material/radio';
import { CashFlowPlannerServiceService } from '../cash-flow-planner-service.service';
import { GridColumnFilterComponent } from '../../shared-components/grid-column-filter/grid-column-filter.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-cash-flow-planner',
  imports: [
    FormsModule,
    MaskitoDirective,
    CurrencyPipe,
    NgxLoadingModule,
    UtilityToolbarComponent,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    KENDO_GRID,
    GridModule,
    HighchartsChartComponent,
    ReactiveFormsModule,
    MatRadioModule,
    GridColumnFilterComponent,
    PercentPipe,
    CurrencyPipe,
  ],
  templateUrl: './cash-flow-planner.component.html',
  styleUrl: './cash-flow-planner.component.scss',
})
export class CashFlowPlannerComponent {
  private mask = inject(MaskService);
  private cfps = inject(CashFlowPlannerServiceService);
  private snacky = inject(MatSnackBar);

  formData: FormGroup = new FormGroup({});
  percentFields = [
    'RequiredWithdrawalCOLA',
    'AnnualWithdrawalPercent',
    'IncomeRisingRate',
    'IncomeRisingRate2',
    'ClientSocialSecurityCOLA',
    'SpouseSocialSecurityCOLA',
    'EquityHoldingsRate',
    'FixedIncomeRate',
    'AnnuityRate',
    'IncomeAnnuityRate',
  ];
  dollarFields = [
    'AnnualIncomeNeed',
    'LifetimeIncome',
    'LifetimeIncome2',
    'ClientSocialSecurityAmount',
    'SpouseSocialSecurityAmount',
    'ClientPensionIncome',
    'SpousePensionIncome',
    'EquityHoldings',
    'FixedIncome',
    'Annuity',
    'IncomeAnnuity',
  ];
  Highcharts: typeof Highcharts = Highcharts;
  dollarMask = this.mask.dollarMaskSpecs();
  percentMask = this.mask.percentMaskSpecs();
  calcComplete = false;
  calcData = {};
  chartData = [];
  tableData = [];
  loading = false;
  tableHeaderValue: number = 0;
  tableColumns = [];
  hidden = [];

  constructor() {
    this.resetFields();
  }

  resetFields() {
    this.formData = new FormGroup({
      AgeAtIssue: new FormControl(60),
      AgeAtRetirement: new FormControl(70),
      MortalityAge: new FormControl(92),
      RequiredWithdrawalCOLA: new FormControl(0),
      WithdrawalMethod: new FormControl('Dollar Amount'),
      IncomePreRetirement: new FormControl('spend'),
      AnnualIncomeNeed: new FormControl(null),
      AnnualWithdrawalPercent: new FormControl(4),
      AgeAtFirstWithdrawal: new FormControl(70),
      LifetimeIncome: new FormControl(null),
      IncomeRisingRate: new FormControl(0),
      AgeAtFirstWithdrawal2: new FormControl(62),
      LifetimeIncome2: new FormControl(null),
      IncomeRisingRate2: new FormControl(0),
      ClientSocialSecurityAge: new FormControl(65),
      ClientSocialSecurityAmount: new FormControl(null),
      ClientSocialSecurityCOLA: new FormControl(0),
      SpouseSocialSecurityAge: new FormControl(65),
      SpouseSocialSecurityAmount: new FormControl(null),
      SpouseSocialSecurityCOLA: new FormControl(0),
      ClientPensionStartAge: new FormControl(65),
      ClientPensionIncome: new FormControl(null),
      SpousePensionStartAge: new FormControl(65),
      SpousePensionIncome: new FormControl(null),
      EquityHoldings: new FormControl(null),
      FixedIncome: new FormControl(null),
      Annuity: new FormControl(null),
      IncomeAnnuity: new FormControl(null),
      EquityHoldingsRate: new FormControl(6),
      FixedIncomeRate: new FormControl(3.5),
      AnnuityRate: new FormControl(0),
      IncomeAnnuityRate: new FormControl(2),
    });
  }

  calculate(data) {
    this.loading = true;
    const percRegex = new RegExp(/%/),
      moneyRegex = new RegExp(/^\$/);

    const controlSetData = data.value;
    const submittedData = {};
    Object.keys(data.value).forEach(x => {
      if (
        data.value[x] !== null &&
        data.value[x] !== '' &&
        data.value[x] !== undefined
      ) {
        if (percRegex.test(data.value[x]) || this.percentFields.includes(x)) {
          submittedData[`RMDCalcInput.${x}`] = this.mask.removePercentMask(
            data.value[x]
          );
        } else if (
          moneyRegex.test(data.value[x]) ||
          this.dollarFields.includes(x)
        ) {
          submittedData[`RMDCalcInput.${x}`] = this.mask.removeMoneyMask(
            data.value[x]
          );
        } else {
          submittedData[`RMDCalcInput.${x}`] = data.value[x];
        }
      } else {
        submittedData[`RMDCalcInput.${x}`] = 0;
        Object.assign(controlSetData, { [x]: 0 });
      }
    });

    this.formData.setValue(controlSetData);

    this.cfps.getCFPCalc(submittedData).subscribe({
      next: res => {
        this.calcComplete = true;
        this.calcData = res;
        this.chartData = [];
        try {
          res['charts'].forEach(chrt => {
            const data = chrt.Data;
            this.chartData.push(data);
          });

          this.tableHeaderValue = res.CFPCalc?.ValueAtDeath;

          this.tableData = res.CFPCalcArray;
          this.tableColumns = res.CFPHeader;
          if (!this.hidden.length) {
            this.hidden = this.tableColumns
              .filter(col => col.Hidden)
              .map(col => col.DataField);
          }
        } catch {
          this.snacky.open(`An unexpected network error occurred.`, 'Close', {
            duration: 4000,
          });
          this.calcComplete = false;
        }
        this.loading = false;
      },
      error: () => {
        this.snacky.open(`An unexpected network error occurred.`, 'Close', {
          duration: 4000,
        });
        this.calcComplete = false;
        this.loading = false;
      },
    });
  }

  editCalc() {
    this.calcComplete = false;
  }

  resetCalc() {
    this.chartData = [];
    this.calcData = {};
    this.calcComplete = false;
    this.tableHeaderValue = 0;

    this.tableColumns = [];
    this.hidden = [];
    this.resetFields();
  }

  externalColumnsUpdated(ev) {
    const fullList = this.tableColumns.map(x => {
      return x.DataField;
    });
    const hidden = fullList.filter(y => !ev.includes(y));

    this.hidden = [...hidden];
  }

  isHidden(columnName: string): boolean {
    return this.hidden.indexOf(columnName) > -1;
  }

  isNum(val: any) {
    return !isNaN(val);
  }
}
